import { AxiosResponse } from "axios";
import * as R from "ramda";
import { take, zip, zipWith } from "ramda";
import { Action, ActionFunction1 } from "redux-actions";
import { all, call, put, select } from "redux-saga/effects";
import { ActionCreator, IAction } from "reduxsauce";
import { AxiosInterceptedResult } from "../../api/interceptors";
import { IResolveResponse } from "../../api/ResolveApi/resolve-api.types";
import { IUsersAnalytics } from "../../App/Analytics/Common";
import { ICourse, IScormPackage } from "../../App/types/common";
import { api } from "../../dependencies";
import {
  analyticsCompactUnitSummaryGetResponseToCompactUnitSummaryUIData,
  analyticsCourseInstanceSummaryGetResponseToCourseInstanceSummaryUIData,
} from "../../parsers/analytics";
import {
  filterSuccessfulResponses,
  hasErrorRecords,
  IClassResponse,
  IClassUIData,
  ICourseSummary,
  IScormPackageUIData,
  resolveClassPostSuccessResponseValueToClassUIData,
  resolveCoursePostSuccessResponseValueToCourseUIData,
  resolveScormPackagePostSuccessResponseValueToScormPackageUIData,
} from "../../parsers/resolve";
import { SagaAction } from "../../sagas/types";
import * as selectors from "../../selectors/selectors";
import { getPercentage } from "../../utils";
import { Err, Ok, Result } from "../../utils/result";
import { SagaFunction } from "../types";
import { ILoadCoursesPayload, ISearchCoursesPayload } from "./dashboard.actions";

export interface IFetchAllDashboardDataFurtherActions {
  onSuccess: ActionCreator;
  onFailure: ActionCreator;
}

export interface IFetchCourseCompletionAnalyticsFurtherActions {
  onSuccess: ActionCreator;
  onFailure: ActionCreator;
}

export interface IFetchCourseCompletionAnalyticsAction extends IAction {
  isClass: boolean;
  courseId: string;
  objectId: string;
}

export interface ILoadMoreCoursesFurtherActions {
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
  onSuccess: ActionFunction1<ILoadCoursesPayload, Action<ILoadCoursesPayload>>;
}

export interface ISearchCoursesFurtherActions {
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
  onSuccess: ActionFunction1<ISearchCoursesPayload, Action<ISearchCoursesPayload>>;
}

// courses
export const fetchAllCourses = async (
  accountId: string,
  organizationId: string
): Promise<Result<string[], { message: string }>> => {
  const response = await api.courses.getDashboardEditableByRelativeTo(accountId, organizationId);

  if (response[0]) {
    return Err({ message: "Failed to fetch courses" });
  }

  return Ok(response[1].data);
};

export const fetchResolveCourses = async (
  accountId: string,
  courses: string[]
): Promise<Result<Array<IResolveResponse<{ courseTitle: string }>>, { message: string }>> => {
  const [err, res]: AxiosInterceptedResult<Array<
    IResolveResponse<{ courseTitle: string }>
  >> = await api.resolve.course.post(accountId, courses);

  if (err || !res) {
    return Err({ message: "Failed to fetch courses" });
  }

  return Ok(res.data);
};

export const getFailedCourses = (
  courseIds: string[],
  courseResponses: Array<IResolveResponse<{ courseTitle: string }>>
): string[] =>
  R.zip(courseIds, courseResponses)
    .filter(([_, response]) => response.code >= 400)
    .map(([id, _]) => id);

// classes
export const fetchClasses = async (
  accountId: string,
  organizationId: string
): Promise<Result<string[], { message: string }>> => {
  const response = await api.classes.getEditableByRelativeTo(accountId, organizationId);

  if (response[0]) {
    return Err({ message: "Failed to fetch classes" });
  }

  return Ok(response[1].data);
};

export const fetchClassesResolve = async (
  accountId: string,
  classesIds: string[]
): Promise<Result<Array<IResolveResponse<IClassResponse>>, { message: string }>> => {
  const [err, res]: AxiosInterceptedResult<Array<IResolveResponse<IClassResponse>>> = await api.resolve.class.post(
    accountId,
    classesIds
  );

  if (err) {
    return Err({ message: err });
  }

  if (res === null) {
    return Err({ message: "Could not fetch classes." });
  }

  return Ok(res.data);
};

export const getClassessData = async (
  accountId: string,
  organizationId: string
): Promise<Result<IClassUIData[], { message: string }>> => {
  const fetchResult: Result<string[], { message: string }> = await fetchClasses(accountId, organizationId);

  if (!fetchResult.ok) {
    return Err(fetchResult.error);
  }

  const resolveResult: Result<Array<IResolveResponse<IClassResponse>>, { message: string }> = await fetchClassesResolve(
    accountId,
    fetchResult.value
  );

  if (!resolveResult.ok) {
    return Err(resolveResult.error);
  }

  if (hasErrorRecords(resolveResult.value)) {
    return Err({ message: "Classes contained errors." });
  }

  const classData: IClassUIData[] = zip(fetchResult.value, resolveResult.value).map(([uuid, datum]) => ({
    ...resolveClassPostSuccessResponseValueToClassUIData(datum),
    classId: uuid,
  }));

  return Ok(classData);
};

// scorm
export const fetchScorms = async (
  accountId: string,
  organizationId: string
): Promise<Result<string[], { message: string }>> => {
  const response: AxiosResponse<string[]> = await api.scormPackages.getEditableByRelativeTo(accountId, organizationId);

  if (response[0]) {
    return Err({ message: "Failed to fetch scorm packages" });
  }

  return Ok(response[1].data);
};

const fetchScormResolve = async (
  accountId: string,
  organizationId: string,
  scormIds: string[]
): Promise<Result<Array<IResolveResponse<IScormPackage>>, { message: string }>> => {
  const result = await api.resolve.scormPackage.post(accountId, scormIds, organizationId);

  if (result[0]) {
    return Err({ message: "Failed to fetch scorm packages" });
  }

  return Ok(result[1].data);
};

export const getScormsData = async (
  accountId: string,
  organizationId: string
): Promise<Result<IScormPackageUIData[], { message: string }>> => {
  const fetchResult: Result<string[], { message: string }> = await fetchScorms(accountId, organizationId);

  if (!fetchResult.ok) {
    return Err(fetchResult.error);
  }

  const resolveResult: Result<Array<IResolveResponse<IScormPackage>>, { message: string }> = await fetchScormResolve(
    accountId,
    organizationId,
    fetchResult.value
  );

  if (!resolveResult.ok) {
    return Err(resolveResult.error);
  }

  if (hasErrorRecords(resolveResult.value)) {
    return Err({ message: "Scorm packages contained errors." });
  }

  const scormPackageData: IScormPackageUIData[] = zipWith(
    (uuid, datum) => ({
      ...resolveScormPackagePostSuccessResponseValueToScormPackageUIData(datum),
      scormPackageId: uuid,
    }),
    fetchResult.value,
    resolveResult.value
  );

  return Ok(scormPackageData);
};

// We don't use this function right now
// since we don't want to fetch course instances
export function* fetchAndResolveAllCourseInstances(accountId: string) {
  const [courseInstanceIdsError, courseInstanceIdsResponse] = yield call(api.courses.getInstancesByAccount, accountId);
  if (courseInstanceIdsError) {
    return [{ message: "Failed to fetch course instances." }, null];
  } else {
    const [courseInstancesError, courseInstancesResponse] = yield call(
      api.resolve.courseInstance.post,
      accountId,
      courseInstanceIdsResponse.data
    );
    if (courseInstancesError) {
      return [{ message: "Failed to fetch course instances." }, null];
    } else if (hasErrorRecords(courseInstancesResponse.data)) {
      return [{ message: "Course instances contained errors." }, null];
    } else {
      return [null, { courseInstanceIdsResponse, courseInstancesResponse }];
    }
  }
}

export const fetchAllDashboardData: (
  furtherActions: IFetchAllDashboardDataFurtherActions,
  limit: number | null
) => SagaFunction = (furtherActions, limit) =>
  function* _fetchAllCourseInstances(_): SagaAction {
    const accountId = yield select(selectors.accountId);
    const organizationId = yield select(selectors.organizationId);

    const {
      courseIdsResult,
      classesResult,
      scormPackagesResult,
    }: {
      courseIdsResult: Result<string[], { message: string }>;
      classesResult: Result<IClassUIData[], { message: string }>;
      scormPackagesResult: Result<IScormPackageUIData[], { message: string }>;
    } = yield all({
      classesResult: call(getClassessData, accountId, organizationId),
      courseIdsResult: call(fetchAllCourses, accountId, organizationId),
      scormPackagesResult: call(getScormsData, accountId, organizationId),
    });

    if (!courseIdsResult.ok) {
      return yield put(furtherActions.onFailure({ message: courseIdsResult.error }));
    }

    if (!classesResult.ok) {
      return yield put(furtherActions.onFailure({ message: classesResult.error }));
    }

    if (!scormPackagesResult.ok) {
      return yield put(furtherActions.onFailure({ message: scormPackagesResult.error }));
    }

    // Now, let's try to resolve courses
    const allCourseIds = courseIdsResult.value;
    const coursesToShow = allCourseIds.slice(0, limit || allCourseIds.length);

    const resolveCourses: Result<Array<IResolveResponse<{ courseTitle: string }>>, { message: string }> = yield call(
      fetchResolveCourses,
      accountId,
      coursesToShow
    );

    if (!resolveCourses.ok) {
      return yield put(furtherActions.onFailure({ message: "Failed to fetch courses" }));
    }

    const coursesData: ICourse[] = filterSuccessfulResponses(coursesToShow, resolveCourses.value).map(
      ([uuid, datum]) => ({
        ...resolveCoursePostSuccessResponseValueToCourseUIData(datum),
        courseId: uuid,
      })
    );

    const failedCourses = getFailedCourses(coursesToShow, resolveCourses.value);

    yield put(
      furtherActions.onSuccess(
        coursesData, // courses
        classesResult.value, // classes
        allCourseIds, // editable by user = all courses
        failedCourses, // failed courses ids
        scormPackagesResult.value // scorm packages
      )
    );
  };

export const fetchCourseCompletionAnalytics: (
  fa: IFetchCourseCompletionAnalyticsFurtherActions
) => SagaFunction<IFetchCourseCompletionAnalyticsAction> = fa =>
  function* _fetchCourseCompletionAnalytics(action): SagaAction {
    const accountId = yield select(selectors.accountId);
    const { isClass, courseId, objectId } = action;
    const setPromise = isClass ? api.analytics.getClassSummarySet : api.analytics.getScormPackageSummarySet;
    const [setErr, setResp] = yield call(setPromise, accountId, courseId, objectId);
    if (setErr) {
      return yield put(fa.onFailure({ message: "Failed to fetch set from course" }));
    }
    const setId: number = setResp.data.summarySetId;
    const usersPromise = isClass ? api.users.getAllForClass : api.users.getAllForScormPackage;
    const [userError, userResp] = yield call(usersPromise, accountId, courseId, objectId);
    if (userError) {
      return yield put(fa.onFailure({ message: "Failed to fetch users" }));
    }
    const compactUnitSummaryPromise = isClass
      ? api.analytics.getClassCompactUnitSummaries
      : api.analytics.getScormPackageCompactUnitSummaries;
    const courseInstancePromise = isClass
      ? api.analytics.getClassCourseInstancesSummaries
      : api.analytics.getScormPackageCourseInstancesSummaries;
    const { courseInstancesResult, compactUnitResult } = yield all({
      compactUnitResult: call(compactUnitSummaryPromise, accountId, courseId, objectId, setId),
      courseInstancesResult: call(courseInstancePromise, accountId, courseId, objectId, setId),
    });
    const [courseInstancesErr, courseInstancesResp, compactUnitErr, compactUnitResp] = [
      ...courseInstancesResult,
      ...compactUnitResult,
    ];
    if (courseInstancesErr || compactUnitErr) {
      return yield put(fa.onFailure({ message: "Failed to retrieve analytics from API." }));
    }
    const compactUnits = analyticsCompactUnitSummaryGetResponseToCompactUnitSummaryUIData(compactUnitResp.data);
    const courseInstances = analyticsCourseInstanceSummaryGetResponseToCourseInstanceSummaryUIData(
      courseInstancesResp.data
    );
    const usersAnalytics: IUsersAnalytics = {};
    Object.entries(courseInstances).forEach(([k, v]) => {
      usersAnalytics[k] = {
        ...v,
        ...compactUnits[v.unitSummaryId],
      };
    });

    const users: Array<[string, string]> = userResp.data;
    const totalUsers = users.length;
    let completed = 0;
    let notStarted = 0;
    let total = 0;
    let completedUsers = 0;
    let notStartedUsers = 0;

    /* For performance issues, we use for(...) and let since map, reduce and
     * others lead to high memory usage and huge load times, related to #335.*/
    for (let i = 0; i < totalUsers; i++) {
      total++;
      const user = users[i];
      const userAnalytics = usersAnalytics[user[0]];

      if (userAnalytics) {
        if (userAnalytics.completed) {
          completed++;
          completedUsers++;
        } else if (!userAnalytics.lastSeen) {
          notStarted++;
          notStartedUsers++;
        }
      } else {
        notStarted++;
        notStartedUsers++;
      }
    }
    const startedUsers = totalUsers - completedUsers - notStarted;
    const started = getPercentage(total - completed - notStarted, total) || 0;
    completed = getPercentage(completed, total) || 0;
    notStarted = getPercentage(notStarted, total) || 0;
    yield put(
      fa.onSuccess({
        completed,
        completedUsers,
        notStarted,
        notStartedUsers,
        started,
        startedUsers,
        totalUsers,
      })
    );
  };

export const loadAllCourses: (fa: ILoadMoreCoursesFurtherActions) => SagaFunction = fa => loadMoreCourses(fa, null);

export const loadMoreCourses: (fa: ILoadMoreCoursesFurtherActions, limit: number | null) => SagaFunction = (
  fa,
  limit
) =>
  function* _loadMoreCourses(_): SagaAction {
    const accountId: string = yield select(selectors.accountId);
    const dashboardAllCourseIds: string[] = yield select(selectors.dashboardAllCourseIds);
    const dashboardShownCourseIds: string[] = yield select(selectors.dashboardShownCourseIds);
    const failedCoursesIds: string[] = yield select(selectors.dashboardFailedCoursesIds);

    const requestedIds: string[] = dashboardAllCourseIds
      .filter(courseId => !dashboardShownCourseIds.includes(courseId) && !failedCoursesIds.includes(courseId))
      .slice(0, limit || dashboardAllCourseIds.length);

    const [resolvedCoursesError, resolvedCoursesResponse]: AxiosInterceptedResult<Array<
      IResolveResponse<ICourseSummary>
    >> = yield call(api.resolve.course.post, accountId, requestedIds);

    if (resolvedCoursesError || !resolvedCoursesResponse) {
      yield put(fa.onFailure({ message: "Could not get courses" }));
      return;
    }

    const courseData: ICourse[] = filterSuccessfulResponses(requestedIds, resolvedCoursesResponse.data).map(
      ([uuid, datum]) => ({
        ...resolveCoursePostSuccessResponseValueToCourseUIData(datum),
        courseId: uuid,
      })
    );

    const failedCourses = getFailedCourses(requestedIds, resolvedCoursesResponse.data);

    yield put(
      fa.onSuccess({
        courses: courseData,
        failedCoursesIds: failedCourses,
      })
    );
    return;
  };

export const searchCoursesByName: (fa: ISearchCoursesFurtherActions, limit: number | null) => SagaFunction = (
  fa,
  limit
) =>
  function* _searchCoursesByName(action: Action<string>): SagaAction {
    const accountId: string = yield select(selectors.accountId);
    const organizationId: string = yield select(selectors.organizationId);
    const [idsErr, idsResp]: AxiosInterceptedResult<string[]> = !!action.payload
      ? yield call(api.autocomplete.getDashboardEligibleCourses, accountId, organizationId, action.payload, "unlimited")
      : yield call(api.courses.getDashboardEditableByRelativeTo, accountId, organizationId);

    if (idsErr || !idsResp) {
      yield put(fa.onFailure({ message: "Could not find course ids" }));
      return;
    }

    const courseIds = idsResp.data;
    const requestedIds = take(limit || courseIds.length, idsResp.data);

    const [resolvedCoursesErr, resolvedCoursesResp]: AxiosInterceptedResult<Array<
      IResolveResponse<ICourseSummary>
    >> = yield call(api.resolve.course.post, accountId, requestedIds);

    if (resolvedCoursesErr || !resolvedCoursesResp) {
      yield put(fa.onFailure({ message: "Could not find course" }));
      return;
    }

    const courses: ICourse[] = filterSuccessfulResponses(courseIds, resolvedCoursesResp.data).map(([uuid, datum]) => ({
      ...resolveCoursePostSuccessResponseValueToCourseUIData(datum),
      courseId: uuid,
    }));

    const failedCoursesIds = getFailedCourses(courseIds, resolvedCoursesResp.data);

    yield put(
      fa.onSuccess({
        courseIds,
        courses,
        failedCoursesIds,
      })
    );
  };
